import { Injectable, signal } from '@angular/core';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';

export interface PermissionOverlayItem {
  icon?: string;
  name: string;
}

/**
 * Small internal controller encapsulating common overlay behavior.
 */
class OverlayController {
  readonly open = signal(false);
  readonly origin = signal<CdkOverlayOrigin | null>(null);

  private _closeTimeout?: ReturnType<typeof setTimeout>;

  openWithOrigin(origin: CdkOverlayOrigin) {
    this.origin.set(origin);
    this.open.set(true);
    this.cancelClose();
  }

  scheduleClose(delay = 0) {
    this.cancelClose();
    this._closeTimeout = setTimeout(() => this.open.set(false), delay);
  }

  cancelClose() {
    if (this._closeTimeout) {
      clearTimeout(this._closeTimeout);
      this._closeTimeout = undefined;
    }
  }

  close() {
    this.open.set(false);
  }
}

@Injectable()
export class TableOverlaysService {
  // Users overlay
  private readonly _usersCtrl = new OverlayController();
  private readonly _usersData = signal<string[]>([]);

  readonly users = {
    open: () => this._usersCtrl.open(),
    origin: () => this._usersCtrl.origin(),
    data: () => this._usersData(),

    openOverlay: (origin: CdkOverlayOrigin, users: string[]) => {
      this._usersCtrl.openWithOrigin(origin);
      this._usersData.set(users ?? []);
    },

    scheduleClose: (delay = 0) => this._usersCtrl.scheduleClose(delay),
    cancelClose: () => this._usersCtrl.cancelClose(),

    close: () => {
      this._usersCtrl.close();
      this._usersData.set([]);
    },
  };

  // Permissions overlay
  private readonly _permCtrl = new OverlayController();
  private readonly _permTitle = signal('');
  private readonly _permItems = signal<PermissionOverlayItem[]>([]);

  readonly perm = {
    open: () => this._permCtrl.open(),
    origin: () => this._permCtrl.origin(),
    title: () => this._permTitle(),
    items: () => this._permItems(),

    openOverlay: (origin: CdkOverlayOrigin, title: string, items: PermissionOverlayItem[] = []) => {
      this._permCtrl.openWithOrigin(origin);
      this._permTitle.set(title ?? '');
      this._permItems.set(items ?? []);
    },

    scheduleClose: (delay = 0) => this._permCtrl.scheduleClose(delay),
    cancelClose: () => this._permCtrl.cancelClose(),

    close: () => {
      this._permCtrl.close();
      this._permTitle.set('');
      this._permItems.set([]);
    },
  };
}
